import { faCertificate, faClipboardCheck, faLock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { Alert, ListGroup, ProgressBar } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router-dom';
import { AppShell } from '../../components/AppShell/AppShell';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { Footer } from '../../components/Footer/Footer';
import { FullscreenLoader } from '../../components/FullscreenLoader/FullscreenLoader';
import { formatSecondsTimestamp, parseDuration } from '../../utils/utils';
import styles from './Course.module.scss';
import { useTranslation } from 'react-i18next';
import { Title } from '../../components/Title/Title';
import { CourseCertificateButton, CourseDetails } from './CourseDetails';
import { getStatusIcon, StatusBadge } from './CourseStatus';
import { HighlightedCard } from '../../components/HighlightedCard/HighlightedCard';

const CourseComponent = () => {
  const { getCourse, courses, subscription, currentUser, userDataFmt } = useFirebase();
  const { id } = useParams<any>();
  const history = useHistory();
  const [course, setCourse] = useState<any>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [courseUnlocked, setCourseUnlocked] = useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    if (!id) history.goBack();
  }, []);

  useEffect(() => {
    (async () => {
      if (!id) return;
      const course = await getCourse(id);
      if (!course) return;
      setCourse(course);
    })();
  }, [id, courses]);

  useEffect(() => {
    if (!userDataFmt) return;

    if (userDataFmt.admin) {
      setCourseUnlocked(true);
      return;
    }

    if (course && course.companies) {
      // The check that the user is in the company is done server-side by the cloud function
      setCourseUnlocked(true);
      return;
    }

    if (!subscription) return;
    try {
      if (subscription.items[0].price.product.metadata.UNLOCK_COURSES == 'yes') {
        setCourseUnlocked(true);
      }
    } catch (e) {
      console.error(e);
    }
  }, [subscription, userDataFmt, course]);

  const getUnitPercentages = (unit) => {
    const getPercentage = (total, partial) => ((partial / total) * 100) | 0;
    const totalTime = unit.content.duration;
    const minimumTime = unit.minimumTime * 1000;
    const presenceTime = (unit.content.timePresence ?? 0) * 1000;
    const presencePercentage = getPercentage(totalTime, presenceTime);
    const minimumPercentage = minimumTime ? Math.max(getPercentage(totalTime, minimumTime) - presencePercentage, 0) : 0;
    const hasLeft = presenceTime < minimumTime;
    return (
      <ProgressBar className={styles.unitProgress}>
        <ProgressBar variant={hasLeft ? 'primary' : 'success'} now={presencePercentage} key={1} />
        {!!minimumTime && <ProgressBar variant={'warning'} now={minimumPercentage} key={2} />}
      </ProgressBar>
    );
  };

  if (!course) return <FullscreenLoader />;

  const timelineItemCount = course.chapters.reduce((count, chapter) => count + chapter.units.length, 0) + (course.tests?.length || 0) + (course.withCertificate ? 1 : 0);
  const hasTimelineConnector = timelineItemCount > 1;

  return (
    <AppShell>
      <div className={'d-flex flex-column flex-grow-1'}>
        <Title>{course.name}</Title>

        <div className={styles.courseLayout}>
          <CourseDetails
            course={course}
            currentUser={currentUser}
            isFetching={isFetching}
            courseUnlocked={courseUnlocked}
            setIsFetching={setIsFetching}
          />

          <div className={styles.courseContent}>

            {!!course.reasonNotAccessible && (

              <div className={styles.courseTimeline}>
                <div className={styles.courseTimelineItem}>
                  <HighlightedCard className="mb-3">{course.reasonNotAccessible}</HighlightedCard>
                </div>
              </div>
            )}

            <div className={`${styles.courseTimeline} ${hasTimelineConnector ? styles.courseTimelineHasConnector : ''}`}>
              {course.chapters.flatMap((chapter) =>
                chapter.units.map((unit) => (
                  <div key={unit.id} className={styles.courseTimelineItem}>
                    <span className={styles.courseTimelineDot}>
                      <FontAwesomeIcon icon={getStatusIcon(unit)} />
                    </span>
                    <div
                      className={`${styles.courseVideo} ${unit.accessible ? styles.courseVideoAccessible : styles.courseVideoLocked}`}
                      onClick={() => (unit.accessible ? history.push(`/course/${id}/watch/${unit.content.id}`) : null)}
                    >
                      <div className={styles.courseVideoPreview}>
                        <ListGroup
                          className={`${styles.courseCard} ${!unit.accessible ? styles.courseListDisabled : ''}`}
                          style={{
                            '--course-thumbnail': `url(${unit.content.thumbnail || unit.content.sm_thumbnail})`,
                            '--course-sm-thumbnail': `url(${unit.content.sm_thumbnail || unit.content.thumbnail})`,
                          } as any}
                        >
                          <ListGroup.Item
                            variant={'dark'}
                            className={`${styles.courseListItem} dark-list-item p-0 border-0`}
                            style={{ backgroundColor: 'transparent' }}
                          >
                            <div className={'d-flex flex-column flex-lg-grow mt-auto'}>
                              <div className={'d-flex flex-column-reverse flex-sm-row align-items-start justify-content-between w-100 mt-2'}>
                                {/*{!!unit.reasonNotAccessible && <small className={'text-muted mr-2'}>{unit.reasonNotAccessible}</small>}*/}
                                <div className={'d-flex mt-auto w-100 align-items-end'}></div>
                              </div>
                            </div>
                          </ListGroup.Item>
                        </ListGroup>
                        {getUnitPercentages(unit)}
                      </div>
                      <div className={`${styles.courseVideoInfo}`}>
                        <h6>
                          <FontAwesomeIcon
                            icon={getStatusIcon(unit)}
                            className={styles.courseVideoStatus}
                            fixedWidth
                          />{' '}
                          {unit.name}
                        </h6>
                        {!!unit.reasonNotAccessible && <Alert variant={'warning'}>{unit.reasonNotAccessible}</Alert>}
                        {unit.description && unit.description.map((x) => <div key={x}>{x}</div>)}
                        <div>
                          {t('course.labels.durata')} <b>{parseDuration(unit.content.duration)}</b>
                        </div>
                        {!!unit.minimumTime && (
                          <div>
                            {t('course.labels.presenzaMinima')}{' '}
                            <b>{parseDuration(unit.minimumTime * 1000)}</b>
                          </div>
                        )}
                        <div>
                          {t('course.labels.tempoPresenza')}{' '}
                          <b>{parseDuration((unit.content.timePresence ?? 0) * 1000)}</b>
                        </div>
                        <div>
                          {t('course.labels.accessi')} <b>{unit.openings || 0}</b>
                        </div>
                        {!!unit.lastOpened && (
                          <div>
                            {t('course.labels.ultimoAccesso')}{' '}
                            <b>{formatSecondsTimestamp(unit.lastOpened._seconds, 'dd MMMM Y HH:mm')}</b>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )),
              )}

              {course.tests?.map((test) => (
                <div
                  key={test.id}
                  className={`${styles.courseTimelineAction} ${test.accessible ? styles.courseTestAccessible : styles.courseTestLocked}`}
                  onClick={() => (test.accessible ? history.push(`/course/${id}/test/${test.id}`) : null)}
                >
                  <span className={styles.courseTimelineDot}>
                    <FontAwesomeIcon icon={getStatusIcon(test, faClipboardCheck)} />
                  </span>
                  <div className={`surface-card ${styles.courseTest}`}>
                    <div className={styles.courseTestHeader}>
                      <h6>{test.name}</h6>
                      <StatusBadge completed={test.completed} status={test.status} className={`${styles.courseBadge} ${!test.completed ? styles.courseBadgeIncomplete : ''}`} />
                    </div>
                    {!!test.reasonNotAccessible && <small className={'text-muted'}>{test.reasonNotAccessible}</small>}
                    <div className={styles.courseTestMeta}>
                      <div>
                        {t('course.labels.domande')} <b>{test.questions.length}</b>
                      </div>
                      <div>
                        {t('course.labels.tentativi')} <b>{test.attempts}</b>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {course.withCertificate && (
                <div className={styles.courseTimelineActions}>
                  <div className={styles.courseTimelineAction}>
                    <span className={styles.courseTimelineDot}>
                      <FontAwesomeIcon icon={course.completed && courseUnlocked && !isFetching ? faCertificate : faLock} />
                    </span>
                    <CourseCertificateButton
                      course={course}
                      currentUser={currentUser}
                      isFetching={isFetching}
                      courseUnlocked={courseUnlocked}
                      setIsFetching={setIsFetching}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <Footer />
      </div>

    </AppShell>
  );
};

export default CourseComponent;
